import { useState, useEffect, useRef, useCallback } from 'react';
import { format, parseISO, startOfMonth, endOfMonth, addMonths } from 'date-fns';
import Calendar from 'react-calendar';
import { api } from '../services/api';

function CalendarView({ agent, duration = 30, onSelectSlot, onBack, embed = false }) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [activeMonth, setActiveMonth] = useState(new Date());
  const [availableDates, setAvailableDates] = useState(new Set());
  const initialLoadDone = useRef(false);

  // Compute max bookable date from agent's maxBookingDays
  const maxDate = agent.maxBookingDays
    ? new Date(new Date().setDate(new Date().getDate() + agent.maxBookingDays))
    : null;

  const fetchMonthAvailability = useCallback(async (monthDate) => {
    try {
      setLoading(true);
      const start = startOfMonth(monthDate);
      let end = endOfMonth(monthDate);

      // Clamp end date to booking horizon
      if (maxDate && end > maxDate) {
        end = maxDate;
      }

      // If start is beyond maxDate, no slots to fetch
      if (maxDate && start > maxDate) {
        setAvailableSlots([]);
        setAvailableDates(new Set());
        return { slots: [], dates: new Set() };
      }

      const data = await api.getAvailability({
        agentId: agent.agentId,
        startDate: start.toISOString(),
        endDate: end.toISOString(),
        duration,
      });

      const slots = data.slots || [];
      setAvailableSlots(slots);

      // Build set of dates that have available slots
      const now = new Date();
      const dates = new Set();
      slots.forEach((slot) => {
        const slotTime = parseISO(slot.startTime);
        if (slotTime > now) {
          dates.add(format(slotTime, 'yyyy-MM-dd'));
        }
      });
      setAvailableDates(dates);

      return { slots, dates };
    } catch (error) {
      console.error('Error loading availability:', error);
      setAvailableSlots([]);
      setAvailableDates(new Set());
      return { slots: [], dates: new Set() };
    } finally {
      setLoading(false);
    }
  }, [agent.agentId, duration]);

  // Initial load - fetch current month and auto-select first available date
  useEffect(() => {
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      (async () => {
        const now = new Date();
        let { dates } = await fetchMonthAvailability(now);

        if (dates.size > 0) {
          // Select the first available date
          const sorted = Array.from(dates).sort();
          setSelectedDate(parseISO(sorted[0]));
        } else {
          // Try next month
          const nextMonth = addMonths(now, 1);
          setActiveMonth(nextMonth);
          const result = await fetchMonthAvailability(nextMonth);
          if (result.dates.size > 0) {
            const sorted = Array.from(result.dates).sort();
            setSelectedDate(parseISO(sorted[0]));
          }
        }
      })();
    }
  }, [agent, fetchMonthAvailability]);

  const handleActiveStartDateChange = ({ activeStartDate }) => {
    setActiveMonth(activeStartDate);
    fetchMonthAvailability(activeStartDate);
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setSlotsLoading(true);
    // Brief visual feedback then show slots
    setTimeout(() => setSlotsLoading(false), 150);
  };

  const getSlotsForDate = (date) => {
    if (!date) return [];
    const dateStr = format(date, 'yyyy-MM-dd');
    const now = new Date();
    return availableSlots.filter((slot) => {
      const slotTime = parseISO(slot.startTime);
      return format(slotTime, 'yyyy-MM-dd') === dateStr && slotTime > now;
    });
  };

  const tileClassName = ({ date, view }) => {
    if (view !== 'month') return null;
    const dateStr = format(date, 'yyyy-MM-dd');
    if (availableDates.has(dateStr)) {
      return 'has-availability';
    }
    return null;
  };

  const tileDisabled = ({ date, view }) => {
    if (view !== 'month') return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return true;
    if (maxDate && date > maxDate) return true;
    return false;
  };

  const selectedSlots = getSlotsForDate(selectedDate);

  return (
    <div>
      {onBack && (
        <div className="mb-6">
          <button
            onClick={onBack}
            className="text-primary-600 hover:text-primary-700 font-medium flex items-center"
          >
            <svg className="h-5 w-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Agent Selection
          </button>
        </div>
      )}

      {!embed && (
        <>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Select a Time with {agent.name}
          </h2>
          <p className="text-gray-600 mb-6">
            Choose a date and time below (times shown in your local timezone)
          </p>
        </>
      )}

      {loading && !availableSlots.length ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading availability...</p>
        </div>
      ) : (
        <div className="flex flex-col md:flex-row gap-6">
          {/* Left panel: Month calendar */}
          <div className="md:w-[360px] flex-shrink-0">
            <div className="bg-white rounded-lg border border-gray-200 p-4 impetra-calendar">
              <Calendar
                onChange={handleDateClick}
                value={selectedDate}
                onActiveStartDateChange={handleActiveStartDateChange}
                activeStartDate={activeMonth}
                tileClassName={tileClassName}
                tileDisabled={tileDisabled}
                minDetail="month"
                calendarType="iso8601"
                prev2Label={null}
                next2Label={null}
                maxDate={maxDate || undefined}
              />
            </div>
            {loading && (
              <div className="flex items-center justify-center mt-3 text-sm text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mr-2"></div>
                Loading...
              </div>
            )}
          </div>

          {/* Right panel: Time slots */}
          <div className="flex-1 min-w-0">
            <div className="bg-white rounded-lg border border-gray-200 p-4 min-h-[320px]">
              {!selectedDate ? (
                <div className="flex items-center justify-center h-full min-h-[280px] text-gray-500">
                  <div className="text-center">
                    <svg className="h-12 w-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="font-medium">Select a date</p>
                    <p className="text-sm mt-1">Choose a date from the calendar to see available times</p>
                  </div>
                </div>
              ) : slotsLoading ? (
                <div className="flex items-center justify-center h-full min-h-[280px]">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : (
                <>
                  <h3 className="font-semibold text-gray-900 mb-4">
                    {format(selectedDate, 'EEEE, MMMM d')}
                  </h3>
                  {selectedSlots.length === 0 ? (
                    <div className="flex items-center justify-center min-h-[240px] text-gray-500">
                      <div className="text-center">
                        <svg className="h-10 w-10 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="font-medium">No availability</p>
                        <p className="text-sm mt-1">Try selecting a different date</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                      {selectedSlots.map((slot, index) => (
                        <button
                          key={index}
                          onClick={() => onSelectSlot(slot)}
                          className="w-full text-center px-4 py-3 text-sm font-medium border border-primary-300 text-primary-700 rounded-lg hover:bg-primary-50 hover:border-primary-500 transition-colors"
                        >
                          {format(parseISO(slot.startTime), 'h:mm a')}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CalendarView;
